import { Button, IconButton } from "../components/rumonai";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { Icon } from "../components/Icon";
import { mediaKind, PublicSurface } from "../panel/PublicSurface";
import {
  channelHref, likeWork, publishedDate, reportViewing, workHref, type Publication, type PublicAgent, platformRequest,
  onPublicationsChanged
} from "./api";
import { PageHeading, PlatformError, PlatformLoading, usePlatformRead } from "./shared";
import { useAutoRefresh } from "../hub/autoRefresh";
import { OwnerEmpty } from "../shell/OwnerEmpty";
import { PageHead } from "../shell/PageHead";
import { Avatar } from "../shell/Avatar";

/** A work's likes: how many, and this viewer's own to give or take back. Its owner sees the count only. */
function Likes({ work }: { work: Publication }) {
  const [held, setHeld] = useState({ likes: work.likes ?? 0, liked: Boolean(work.liked) });
  const pending = useRef(false);
  // A fresh read of the work brings the count others gave meanwhile.
  useEffect(() => {
    if (!pending.current) setHeld({ likes: work.likes ?? 0, liked: Boolean(work.liked) });
  }, [work.likes, work.liked]);
  const content = <>
    <Icon name={held.liked ? "liked" : "like"} size={20} />
    <span className="visually-hidden">赞 </span>
    <span>{held.likes}</span>
  </>;
  if (work.mine) return <span className="work-likes">{content}</span>;
  const toggle = () => {
    if (pending.current) return;
    pending.current = true;
    const before = held;
    const liked = !before.liked;
    setHeld({ likes: Math.max(0, before.likes + (liked ? 1 : -1)), liked });
    likeWork(work.id, liked).then(setHeld, () => setHeld(before)).finally(() => { pending.current = false; });
  };
  return <Button variant="ghost" type="button" className="work-likes" aria-pressed={held.liked} onClick={toggle}>{content}</Button>;
}

export function WorkCard({ work }: { work: Publication }) {
  return <article className="platform-work-card">
    <a
      href={workHref(work.id)}
      className={`platform-work-cover${work.cover_url ? " has-image" : ""}`}
      aria-label={`体验：${work.title}`}
    >
      {work.cover_url ? (
        <img src={work.cover_url} alt="" loading="lazy" />
      ) : (
        <>
          <Icon name="wall" size={38} />
          <span>{work.action_label || "进入作品"}</span>
        </>
      )}
      {work.featured ? <span className="platform-featured">精选</span> : null}
      <span className="platform-cover-arrow"><Icon name="arrow" /></span>
    </a>
    <div className="platform-work-copy">
      <a href={channelHref(work.agent_id)} className="platform-agent-credit">{work.agent_name}</a>
      <h2><a href={workHref(work.id)}>{work.title}</a></h2>
      {work.description ? <p>{work.description}</p> : null}
      <a className="platform-work-action" href={workHref(work.id)}>
        {work.action_label || "进入作品"}
        <Icon name="arrow" size={17} />
      </a>
    </div>
  </article>;
}

const STEPS = new Map([["ArrowDown", 1], ["PageDown", 1], ["j", 1], ["ArrowUp", -1], ["PageUp", -1], ["k", -1]]);
// Wheel events closer together than this (ms) are one gesture: a turn of the
// wheel, or a trackpad stroke with its trailing momentum.
const WHEEL_GESTURE_GAP = 200;

/** `signedIn`: whether the viewer has an account; one who has none is not sent to a page of their own works (the sidebar already offers the way in). */
export function SquarePage({ active = true, signedIn = true }: { active?: boolean; signedIn?: boolean }) {
  const reading = usePlatformRead<{ publications: Publication[]; next_cursor?: string | null }>("/api/public/square");
  useEffect(() => onPublicationsChanged(reading.reload), [reading.reload]);
  const [extra, setExtra] = useState<Publication[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const [focused, setFocused] = useState(0);
  // Once the viewer turns sound on, the next works start with sound too.
  const [sound, setSound] = useState(false);
  const moreLock = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    setExtra([]);
    setCursor(reading.data?.next_cursor || null);
    setMoreError(null);
    generation.current++;
  }, [reading.data]);
  const loadMore = async () => {
    if (!cursor || moreLock.current) return;
    const version = generation.current;
    moreLock.current = true; setLoadingMore(true); setMoreError(null);
    try {
      const page = await platformRequest<{
        publications: Publication[];
        next_cursor: string | null
      }>(`/api/public/square?cursor=${encodeURIComponent(cursor)}`);
      if (version === generation.current) {
        setExtra(previous => [
          ...previous,
          ...page.publications.filter(item =>
            !previous.some(held => held.id === item.id) &&
            !reading.data?.publications.some(held => held.id === item.id)
          )
        ]);
        setCursor(page.next_cursor);
      }
    } catch (error) { setMoreError(error); }
    finally { moreLock.current = false; setLoadingMore(false); }
  };
  const loadMoreRef = useRef(loadMore); loadMoreRef.current = loadMore;
  const works = [...(reading.data?.publications || []), ...extra];
  const count = works.length;
  // The next page loads while the viewer is still two works from the end.
  useEffect(() => {
    if (active && cursor && !moreError && focused >= works.length - 3) void loadMoreRef.current();
  }, [active, cursor, moreError, focused, works.length]);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  const feed = useRef<HTMLElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(focused); focusedRef.current = focused;
  // Where the last key press sent the viewer, until the feed gets there: two
  // quick presses move two works, not one.
  const heading = useRef<number | null>(null);
  useEffect(() => { if (heading.current === focused) heading.current = null; }, [focused]);
  const forgetHeading = () => { heading.current = null; };
  const move = useCallback((step: number) => {
    const view = scroller.current;
    if (!view) return;
    const target = Math.max(0, Math.min(view.children.length - 1, (heading.current ?? focusedRef.current) + step));
    heading.current = target;
    view.scrollTo({ top: (view.children[target] as HTMLElement).offsetTop });
  }, []);
  useEffect(() => {
    if (!active) return;
    const press = (event: KeyboardEvent) => {
      const step = STEPS.get(event.key);
      if (!step || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      // The top bar, the navigation and anything typed into keep their keys.
      if (target && target !== document.body && !feed.current?.contains(target)) return;
      if (target?.closest("input, textarea, select") || target?.isContentEditable) return;
      event.preventDefault();
      move(step);
    };
    window.addEventListener("keydown", press);
    return () => window.removeEventListener("keydown", press);
  }, [active, move]);
  // One wheel or trackpad gesture moves one work. Left to snapping, a stroke
  // shorter than half a work falls back to the work it started on.
  useEffect(() => {
    const section = feed.current;
    if (!section) return;
    let last = -Infinity;
    const turn = (event: WheelEvent) => {
      // Pinch zoom and sideways strokes are not the viewer's way down the feed.
      if (!scroller.current || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      if (event.timeStamp - last > WHEEL_GESTURE_GAP) move(Math.sign(event.deltaY));
      last = event.timeStamp;
    };
    section.addEventListener("wheel", turn, { passive: false });
    return () => section.removeEventListener("wheel", turn);
  }, [move]);
  // A hidden feed loses its scroll offset: coming back lands on the same work.
  useLayoutEffect(() => {
    const view = scroller.current;
    const card = view?.children[focusedRef.current] as HTMLElement | undefined;
    if (active && view && card) view.scrollTo({ top: card.offsetTop, behavior: "instant" });
  }, [active]);
  // The stop after the last work is a place of its own: moving up from it lands on the last work.
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = end.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.intersectionRatio >= 0.6)) setFocused(count);
    }, { root: scroller.current, threshold: [0.6] });
    observer.observe(target);
    return () => observer.disconnect();
  }, [count]);
  // A later page that arrives while the viewer waits at the end continues at its first work.
  // Left alone, the browser keeps the end in view, with the new works above it.
  const extraShown = useRef(0);
  useLayoutEffect(() => {
    const added = extra.length - extraShown.current;
    extraShown.current = extra.length;
    const start = count - added;
    const view = scroller.current;
    const first = view?.children[start] as HTMLElement | undefined;
    const waiting = focusedRef.current >= start;
    if (added > 0 && view && first && waiting) view.scrollTo({ top: first.offsetTop, behavior: "instant" });
  }, [extra]);
  // A refresh starts again from the newest work, once the fresh list is on screen: going
  // there earlier would leave a newly published work above the viewer.
  const fromTop = useRef(false);
  useLayoutEffect(() => {
    if (!fromTop.current || !reading.data) return;
    fromTop.current = false;
    scroller.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [reading.data]);
  const refresh = () => {
    heading.current = null;
    fromTop.current = true;
    reading.reload();
  };
  // At the newest work the feed reads itself again, so a work made public meanwhile appears there. A viewer
  // further down is not moved: coming back to the newest (below) is what reads for them.
  useAutoRefresh(reading.reload, { active: active && focused === 0 && extra.length === 0 && !loadingMore });

  return <section className="square-feed" ref={feed} aria-label="广场作品流">
    <div className="square-head"><PageHead title="广场" /></div>
    {!reading.data ? (
      <div className="square-feed-state">
        {reading.error ? <PlatformError error={reading.error} retry={reading.reload} />
          : reading.loading ? <PlatformLoading label="正在读取作品" /> : null}
      </div>
    ) : !count ? (
      <div className="square-feed-state">
        {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
        <OwnerEmpty icon="wall" action={signedIn ? <a className="btn" href="/#/agents">回到我的 IP，继续创作</a> : undefined}>还没有公开的作品。IP 发布的作品会出现在这里。</OwnerEmpty>
      </div>
    ) : <>
      {reading.error ? (
        <div className="square-feed-alert"><PlatformError error={reading.error} retry={reading.reload} /></div>
      ) : null}
      <div className="square-feed-scroller" ref={scroller} onTouchStart={forgetHeading} onPointerDown={forgetHeading}>
        {works.map((work, index) => (
          <FeedWork
            key={work.id}
            work={work}
            index={index}
            focused={focused}
            root={scroller}
            shown={active}
            pageVisible={pageVisible}
            sound={sound}
            onSound={setSound}
            onFocus={() => setFocused(index)}
          />
        ))}
        <div className="square-feed-end" ref={end}>
          {moreError ? <PlatformError error={moreError} retry={() => void loadMore()} /> : cursor ? (
            <Button variant="secondary" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "正在加载…" : "继续浏览"}
            </Button>
          ) : (
            <div className="square-feed-done">
              <p>已经看完了</p>
              <Button variant="secondary" type="button"  disabled={reading.loading} onClick={refresh}>
                <Icon name="up" />
                {reading.loading ? "正在读取…" : "回到最新"}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="square-feed-nav">
        <IconButton name="up" label="上一个作品" disabled={focused <= 0} onClick={() => move(-1)} />
        <IconButton name="down" label="下一个作品" disabled={focused >= count} onClick={() => move(1)} />
      </div>
    </>}
  </section>;
}

function FeedWork({ work, index, focused, root, shown, pageVisible, sound, onSound, onFocus }: {
  work: Publication;
  index: number;
  focused: number;
  root: RefObject<HTMLDivElement>;
  shown: boolean;
  pageVisible: boolean;
  sound: boolean;
  onSound: (on: boolean) => void;
  onFocus: () => void
}) {
  const element = useRef<HTMLElement>(null);
  const workArea = useRef<HTMLDivElement>(null);
  // This keyed item is one immutable publication. A catalogue re-read must not
  // replace its page declaration and reset the bridge's in-flight actions.
  const content = useRef(work);
  const titleId = useId();
  const onFocusRef = useRef(onFocus); onFocusRef.current = onFocus;
  const [inView, setInView] = useState(false);
  const [live, setLive] = useState(false);
  // The room below the picture where the work's player puts its sound switch.
  const [controls, setControls] = useState<HTMLDivElement | null>(null);
  const current = index === focused;
  const mounted = Math.abs(index - focused) <= 1;
  const page = !mediaKind(work.entry);
  useEffect(() => {
    const target = element.current;
    if (!target) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries.find(value => value.target === target);
      if (!entry) return;
      setInView(entry.isIntersecting);
      // Most of the card on screen: this is the work the viewer is on.
      if (entry.intersectionRatio >= 0.6) onFocusRef.current();
    }, { root: root.current, threshold: [0, 0.6] });
    observer.observe(target);
    return () => observer.disconnect();
  }, [root]);
  // Moving to another work puts a page back to the untouched state.
  useEffect(() => { if (!current) setLive(false); }, [current]);
  // Until the viewer starts it, a page renders but takes no touch, so the feed stays swipeable.
  useEffect(() => { workArea.current?.toggleAttribute("inert", page && !live); }, [page, live]);
  const visible = inView && shown && pageVisible;
  return <article className={`square-card${page ? "" : " is-media"}`} ref={element} aria-labelledby={titleId}>
    <div className="square-card-frame">
      <div className="square-card-stage">
        <div className="square-card-work" ref={workArea}>
          {mounted ? (
            <PublicSurface
              publication={content.current}
              mode="feed"
              visible={visible}
              playing={current && visible}
              release={!shown}
              sound={sound}
              onSound={onSound}
              controls={controls}
            />
          ) : (
            <div className="square-card-placeholder" aria-hidden="true">
              {work.cover_url ? <img src={work.cover_url} alt="" loading="lazy" /> : <Icon name="wall" size={30} />}
            </div>
          )}
        </div>
        {page && current && !live ? (
          <Button variant="primary" type="button" className="square-card-start" onClick={() => {
            setLive(true);
            // Starting a page is what watching is to a film: Square counts the viewer.
            if (!work.mine) reportViewing(work.id);
          }}>
            开始互动
          </Button>
        ) : null}
      </div>
      <div className="square-card-info">
        <div className="square-card-copy">
          <a className="platform-agent-credit" href={channelHref(work.agent_id)}>{work.agent_name}</a>
          <h2 id={titleId}>{work.title}</h2>
          {work.description ? <p>{work.description}</p> : null}
        </div>
        <div className="square-card-side">
          <div className="square-card-controls">
            <Likes work={work} />
            {page ? null : <div className="square-card-sound" ref={setControls} />}
          </div>
          <a className="btn btn-accent square-card-open" href={workHref(work.id)} aria-label={`打开作品：${work.title}`}>
            打开作品
            <Icon name="arrow" size={16} />
          </a>
        </div>
      </div>
    </div>
  </article>;
}

export function ChannelPage({ id }: { id: string }) {
  const reading = usePlatformRead<{
    agent: PublicAgent;
    publications: Publication[]
  }>(`/api/public/agents/${encodeURIComponent(id)}`);
  return <>
    <a className="platform-back" href="/#/square"><Icon name="back" />回到广场</a>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在打开主页" /> : null}
    {reading.data ? (
      <>
        <div className="platform-channel-intro">
          <Avatar name={reading.data.agent.name} identity={id} className="platform-agent-avatar" />
          <PageHeading title={reading.data.agent.name}>
            <p>{reading.data.agent.description || "从它的作品，认识它的判断与风格。"}</p>
          </PageHeading>
        </div>
        <div className="platform-section-title">
          <h2>作品与体验</h2>
          <span>{reading.data.publications.length} 件作品</span>
        </div>
        {reading.data.publications.length ? (
          <div className="platform-work-grid">
            {reading.data.publications.map(work => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        ) : (
          <OwnerEmpty icon="wall" action={<a className="btn" href="/#/square">回到广场</a>}>这个 IP 还没有公开的作品。</OwnerEmpty>
        )}
      </>
    ) : null}
  </>;
}

export function PublicWorkPage({ id }: { id: string }) {
  const reading = usePlatformRead<{ publication: Publication }>(`/api/public/works/${encodeURIComponent(id)}`);
  const opened = reading.data?.publication;
  // Opening a page is what watching is to a film: Square counts the viewer.
  // A film tells Square itself, from its player.
  const openedPage = opened && !mediaKind(opened.entry) && !opened.mine ? opened.id : "";
  useEffect(() => { if (openedPage) reportViewing(openedPage); }, [openedPage]);
  return <>
    <header className="platform-work-heading">
      <a className="platform-back" href="/#/square">
        <Icon name="back" />
        <span>广场</span>
      </a>
      {reading.data ? (
        <>
          <div className="platform-work-source">
            <a className="platform-agent-credit" href={channelHref(reading.data.publication.agent_id)}>
              {reading.data.publication.agent_name}
            </a>
            <h1>{reading.data.publication.title}</h1>
          </div>
          <span className="platform-published-date">{publishedDate(reading.data.publication.created_at)}</span>
          <Likes key={reading.data.publication.id} work={reading.data.publication} />
        </>
      ) : null}
    </header>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在打开作品" /> : null}
    {reading.data ? (
      <section className="platform-public-surface" aria-label={reading.data.publication.title}>
        <PublicSurface publication={reading.data.publication} />
      </section>
    ) : null}
  </>;
}
